import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react'
import { getPaymentStatus, type PaymentStatus } from '@/services/orderService'
import { useCart } from '@/services/cartService'
import applyBg from '@/pages/JobApplication/assets/apply-bg.png'

// หน้าที่ TEP พาลูกค้ากลับมาหลังหน้าจ่ายเงินของ Gateway — /payment/result/:orderId
// ⚠️ การเข้าหน้านี้ไม่ได้แปลว่าจ่ายสำเร็จ (ลูกค้าอาจกดยกเลิก/บัตรไม่ผ่าน/พิมพ์ URL เอง) — ต้องรอ backend ยืนยันจาก Gateway เสมอ
// ถามสถานะทุก 5 วินาที (backend ถาม Gateway ให้เองถ้า webhook ยังไม่มา) หยุดเมื่อได้ผลชัดเจนหรือครบเวลา

const POLL_INTERVAL_MS = 5000
const MAX_POLLS = 60   // 5 นาที — เลยนี้แล้วยังไม่ยืนยัน แปลว่ายังไม่ได้จ่ายหรือ Gateway ช้า ให้ลูกค้าไปดูที่ประวัติคำสั่งซื้อ

type View = 'checking' | 'paid' | 'review' | 'failed' | 'expired' | 'timeout'

const viewOf = (s: PaymentStatus): View => {
  if (s.status === 'PAID') return 'paid'
  if (s.needs_review) return 'review'
  if (s.status === 'FAILED' || s.status === 'REFUNDED') return 'failed'
  if (s.expired) return 'expired'
  return 'checking'
}

export default function PaymentResultPage() {
  const { orderId } = useParams()
  const id = Number(orderId)
  const cart = useCart()
  const [view, setView] = useState<View>('checking')
  const [status, setStatus] = useState<PaymentStatus | null>(null)
  const [error, setError] = useState('')
  const [round, setRound] = useState(0)   // เปลี่ยนค่า = เริ่มถามใหม่ (ปุ่ม "ตรวจสอบอีกครั้ง")
  const cartRefreshed = useRef(false)
  // เริ่มถามสถานะใหม่ (ปุ่ม "ตรวจสอบอีกครั้ง" / ที่จริงจ่ายแล้วหลังขอ QR ใหม่) — รีเซ็ตหน้าจอที่นี่ ไม่ทำใน effect
  const restartChecking = () => { setView('checking'); setError(''); setRound((n) => n + 1) }

  const refreshCart = cart.refresh
  const finish = useCallback((s: PaymentStatus) => {
    setStatus(s)
    const v = viewOf(s)
    setView(v)
    if (v === 'paid' && !cartRefreshed.current) {
      cartRefreshed.current = true
      void refreshCart()   // จ่ายสำเร็จ → server ลบรายการที่ซื้อออกจากตะกร้าแล้ว ดึงตะกร้าใหม่
    }
    return v !== 'checking'
  }, [refreshCart])

  useEffect(() => {
    if (!Number.isInteger(id) || id <= 0) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let polls = 0

    const tick = async () => {
      polls += 1
      try {
        const s = await getPaymentStatus(id)
        if (cancelled) return
        setError('')
        if (finish(s)) return
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'ตรวจสอบสถานะไม่สำเร็จ')   // ล้มชั่วคราวไม่หยุดถาม
      }
      if (polls >= MAX_POLLS) return setView('timeout')
      timer = setTimeout(tick, POLL_INTERVAL_MS)
    }
    void tick()
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [id, round, finish])

  if (!Number.isInteger(id) || id <= 0) {
    return <p className="pt-32 text-center text-sm text-text-muted">ไม่พบคำสั่งซื้อ</p>
  }

  const ordersLink = (label: string, primary = true) => (
    <Link
      to="/account?tab=orders"
      className={`rounded-full px-8 py-3 text-sm font-semibold ${primary ? 'bg-indigo-500 text-white hover:bg-violet-400' : 'border border-border text-text hover:border-accent/40'}`}
    >
      {label}
    </Link>
  )

  return (
    <div className="relative isolate flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#FAFAFB] px-4 pb-16 pt-24 text-center">
      <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />

      {view === 'checking' && (
        <>
          <Loader2 className="relative mb-4 h-14 w-14 animate-spin text-indigo-500" />
          <h1 className="relative text-2xl font-bold text-text">กำลังตรวจสอบการชำระเงิน</h1>
          <p className="relative mt-2 max-w-md text-sm text-text-muted">กรุณารอสักครู่ ห้ามปิดหน้านี้ ระบบกำลังรอการยืนยันจากผู้ให้บริการชำระเงิน</p>
          {error && <p className="relative mt-3 text-xs text-amber-600">{error} — กำลังลองใหม่</p>}
        </>
      )}

      {view === 'paid' && (
        <>
          <CheckCircle2 className="relative mb-4 h-14 w-14 text-emerald-500" />
          <h1 className="relative text-2xl font-bold text-text">ชำระเงินสำเร็จ</h1>
          {status?.order_number && <p className="relative mt-1 text-sm text-text-muted">เลขที่คำสั่งซื้อ {status.order_number}</p>}
          <div className="relative mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/account?tab=courses" className="rounded-full bg-indigo-500 px-8 py-3 text-sm font-semibold text-white hover:bg-violet-400">ไปที่คอร์สเรียนของฉัน</Link>
            {ordersLink('ดูประวัติคำสั่งซื้อ', false)}
          </div>
        </>
      )}

      {view === 'review' && (
        <>
          <Clock className="relative mb-4 h-14 w-14 text-amber-500" />
          <h1 className="relative text-2xl font-bold text-text">ได้รับการชำระเงินแล้ว กำลังตรวจสอบ</h1>
          <p className="relative mt-2 max-w-md text-sm text-text-muted">
            ระบบได้รับเงินของคุณแล้ว แต่ยังดำเนินการให้อัตโนมัติไม่สำเร็จ ทีมงานกำลังตรวจสอบและจะติดต่อกลับ หากมีข้อสงสัยกรุณาติดต่อทีมงานพร้อมแจ้งเลขคำสั่งซื้อ
          </p>
          <div className="relative mt-6">{ordersLink('ดูประวัติคำสั่งซื้อ')}</div>
        </>
      )}

      {(view === 'failed' || view === 'expired') && (
        <>
          <XCircle className="relative mb-4 h-14 w-14 text-rose-500" />
          <h1 className="relative text-2xl font-bold text-text">{view === 'expired' ? 'ลิงก์ชำระเงินหมดอายุ' : 'ชำระเงินไม่สำเร็จ'}</h1>
          <p className="relative mt-2 max-w-md text-sm text-text-muted">ยังไม่มีการตัดเงิน คุณสามารถกลับไปเลือกสินค้าและชำระเงินใหม่ได้</p>
          <div className="relative mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/cart" className="rounded-full bg-indigo-500 px-8 py-3 text-sm font-semibold text-white hover:bg-violet-400">กลับไปตะกร้า</Link>
            {ordersLink('ดูประวัติคำสั่งซื้อ', false)}
          </div>
        </>
      )}

      {view === 'timeout' && (
        <>
          <Clock className="relative mb-4 h-14 w-14 text-amber-500" />
          <h1 className="relative text-2xl font-bold text-text">ยังไม่ได้รับการยืนยันการชำระเงิน</h1>
          <p className="relative mt-2 max-w-md text-sm text-text-muted">
            หากคุณชำระเงินแล้ว ผลอาจใช้เวลาสักครู่ ระบบจะอัปเดตคำสั่งซื้อให้อัตโนมัติ ตรวจสอบสถานะได้ที่ประวัติคำสั่งซื้อ
          </p>
          <div className="relative mt-6 flex flex-wrap justify-center gap-3">
            <button type="button" onClick={restartChecking} className="rounded-full bg-indigo-500 px-8 py-3 text-sm font-semibold text-white hover:bg-violet-400">ตรวจสอบอีกครั้ง</button>
            {ordersLink('ดูประวัติคำสั่งซื้อ', false)}
          </div>
        </>
      )}
    </div>
  )
}
